// src/components/CategoryManager.js
import React, { useState, useEffect, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import { RestaurantContext } from "../contexts/RestaurantContext";
import { menuService } from "../services/menuService";
import { enhanceDrinkWithAI, getCategoryFields } from "../services/aiService";
import "./CategoryManager.css";

// Helper component for rendering AI enhancement fields
const AIEnhancementDisplay = ({ aiEnhancements, categoryFields }) => {
  if (!aiEnhancements) return null;

  return (
    <div className="ai-info">
      {categoryFields.slice(0, 3).map(
        (field) =>
          aiEnhancements[field.key] && (
            <div key={field.key} className="ai-field">
              <strong>{field.label}:</strong>{" "}
              {Array.isArray(aiEnhancements[field.key])
                ? aiEnhancements[field.key].join(", ")
                : aiEnhancements[field.key]}
            </div>
          )
      )}
    </div>
  );
};

// Helper component for item status badges
const ItemStatusBadges = ({ item }) => (
  <div className="item-status">
    {item.inStock === false && (
      <span className="out-of-stock">Out of Stock</span>
    )}
    {item.aiEnhancements?.aiEnhanced && (
      <span className="ai-enhanced">✨ AI Enhanced</span>
    )}
  </div>
);

// Helper component for item actions
const ItemActions = ({ item, onEdit, onDelete }) => (
  <div className="item-actions">
    <button className="edit-button" onClick={() => onEdit(item)}>
      Edit
    </button>
    <button className="delete-button" onClick={() => onDelete(item.id)}>
      Delete
    </button>
  </div>
);

// Helper component for individual item card
const ItemCard = ({ item, categoryFields, onEdit, onDelete }) => (
  <div className="item-card">
    <div className="item-header">
      <h4>{item.name}</h4>
      <span className="item-price">${item.price}</span>
    </div>
    <p className="item-description">{item.description}</p>
    <AIEnhancementDisplay
      aiEnhancements={item.aiEnhancements}
      categoryFields={categoryFields}
    />
    <ItemStatusBadges item={item} />
    <ItemActions item={item} onEdit={onEdit} onDelete={onDelete} />
  </div>
);

// Helper component for items list
const ItemsList = ({ items, category, categoryFields, onEdit, onDelete }) => {
  if (items.length === 0) {
    return (
      <p className="no-items">
        No {category.toLowerCase()}s found. Add your first one!
      </p>
    );
  }

  return (
    <div className="items-grid">
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          categoryFields={categoryFields}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

const CategoryManager = ({ category, onClose }) => {
  const { restaurant } = useContext(RestaurantContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    price: "",
    description: "",
    category: category,
    type: category.toLowerCase(),
    inStock: true,
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadItems = useCallback(async () => {
    if (!restaurant?.id) return;

    try {
      setLoading(true);
      const allItems = await menuService.getMenuItems(restaurant.id);
      const categoryItems = allItems.filter(
        (item) =>
          item.category?.toLowerCase() === category.toLowerCase() ||
          item.type?.toLowerCase() === category.toLowerCase()
      );
      setItems(categoryItems);
    } catch (error) {
      console.error("Error loading items:", error);
      setError("Failed to load items");
    } finally {
      setLoading(false);
    }
  }, [category, restaurant?.id]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAddItem = async () => {
    if (!restaurant?.id || !newItem.name.trim()) return;

    try {
      const itemToAdd = {
        ...newItem,
        restaurantId: restaurant.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await menuService.addMenuItem(restaurant.id, itemToAdd);
      setSuccess(`${category} added successfully!`);
      setNewItem({
        name: "",
        price: "",
        description: "",
        category: category,
        type: category.toLowerCase(),
        inStock: true,
      });
      setShowAddForm(false);
      loadItems();
    } catch (error) {
      console.error("Error adding item:", error);
      setError("Failed to add item");
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({ ...item });
    setShowAddForm(false);
  };

  const handleUpdateItem = async () => {
    if (!restaurant?.id || !editingItem) return;

    try {
      const updatedItem = {
        ...editingItem,
        updatedAt: new Date(),
      };

      await menuService.updateMenuItem(
        restaurant.id,
        editingItem.id,
        updatedItem
      );
      setSuccess(`${category} updated successfully!`);
      setEditingItem(null);
      loadItems();
    } catch (error) {
      console.error("Error updating item:", error);
      setError("Failed to update item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (
      !restaurant?.id ||
      !window.confirm("Are you sure you want to delete this item?")
    )
      return;

    try {
      await menuService.deleteMenuItem(restaurant.id, itemId);
      setSuccess(`${category} deleted successfully!`);
      loadItems();
    } catch (error) {
      console.error("Error deleting item:", error);
      setError("Failed to delete item");
    }
  };

  const handleAIEnhance = async (item) => {
    if (!item) return;

    setAiLoading(true);
    setError("");

    try {
      const enhanced = await enhanceDrinkWithAI(item, category.toLowerCase());

      if (editingItem) {
        setEditingItem({
          ...editingItem,
          ...enhanced,
          aiEnhancements: {
            ...editingItem.aiEnhancements,
            ...enhanced,
          },
        });
      } else {
        setNewItem({
          ...newItem,
          ...enhanced,
          aiEnhancements: enhanced,
        });
      }

      setSuccess("AI enhancement completed!");
    } catch (error) {
      console.error("AI enhancement failed:", error);
      setError("AI enhancement failed: " + error.message);
    } finally {
      setAiLoading(false);
    }
  };

  const renderFormField = (field, value, onChange) => {
    switch (field.type) {
      case "array":
        return (
          <textarea
            value={Array.isArray(value) ? value.join(", ") : value || ""}
            onChange={(e) =>
              onChange(e.target.value.split(", ").filter((v) => v.trim()))
            }
            placeholder={`Enter ${field.label.toLowerCase()} separated by commas`}
            rows={2}
          />
        );

      case "textarea":
        return (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
          />
        );

      case "select":
        return (
          <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select {field.label}</option>
            {field.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  const categoryFields = getCategoryFields(category);

  if (loading) {
    return (
      <div className="category-manager loading">Loading {category}s...</div>
    );
  }

  return (
    <div className="category-manager">
      <div className="category-header">
        <h2>Manage {category}s</h2>
        <div className="header-actions">
          <button
            className="add-button"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            Add New {category}
          </button>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Add New Item Form */}
      {showAddForm && (
        <div className="item-form">
          <h3>Add New {category}</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) =>
                  setNewItem({ ...newItem, name: e.target.value })
                }
                placeholder={`${category} name`}
              />
            </div>
            <div className="form-group">
              <label>Price</label>
              <input
                type="text"
                value={newItem.price}
                onChange={(e) =>
                  setNewItem({ ...newItem, price: e.target.value })
                }
                placeholder="$0.00"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={newItem.description}
              onChange={(e) =>
                setNewItem({ ...newItem, description: e.target.value })
              }
              placeholder={`Describe this ${category.toLowerCase()}`}
              rows={3}
            />
          </div>

          {/* Category-specific fields */}
          {categoryFields.map((field) => (
            <div key={field.key} className="form-group">
              <label>{field.label}</label>
              {renderFormField(
                field,
                newItem.aiEnhancements?.[field.key] || newItem[field.key],
                (value) =>
                  setNewItem({
                    ...newItem,
                    [field.key]: value,
                    aiEnhancements: {
                      ...newItem.aiEnhancements,
                      [field.key]: value,
                    },
                  })
              )}
            </div>
          ))}

          <div className="form-actions">
            <button
              className="ai-button"
              onClick={() => handleAIEnhance(newItem)}
              disabled={aiLoading || !newItem.name.trim()}
            >
              {aiLoading ? "Enhancing..." : "✨ AI Enhance"}
            </button>
            <button className="save-button" onClick={handleAddItem}>
              Add {category}
            </button>
            <button
              className="cancel-button"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Item Form */}
      {editingItem && (
        <div className="item-form editing">
          <h3>Edit {editingItem.name}</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={editingItem.name}
                onChange={(e) =>
                  setEditingItem({ ...editingItem, name: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Price</label>
              <input
                type="text"
                value={editingItem.price}
                onChange={(e) =>
                  setEditingItem({ ...editingItem, price: e.target.value })
                }
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={editingItem.description}
              onChange={(e) =>
                setEditingItem({ ...editingItem, description: e.target.value })
              }
              rows={3}
            />
          </div>

          {/* Category-specific fields */}
          {categoryFields.map((field) => (
            <div key={field.key} className="form-group">
              <label>{field.label}</label>
              {renderFormField(
                field,
                editingItem.aiEnhancements?.[field.key] ||
                  editingItem[field.key],
                (value) =>
                  setEditingItem({
                    ...editingItem,
                    [field.key]: value,
                    aiEnhancements: {
                      ...editingItem.aiEnhancements,
                      [field.key]: value,
                    },
                  })
              )}
            </div>
          ))}

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={editingItem.inStock !== false}
                onChange={(e) =>
                  setEditingItem({ ...editingItem, inStock: e.target.checked })
                }
              />
              In Stock
            </label>
          </div>

          <div className="form-actions">
            <button
              className="ai-button"
              onClick={() => handleAIEnhance(editingItem)}
              disabled={aiLoading}
            >
              {aiLoading ? "Enhancing..." : "✨ AI Enhance"}
            </button>
            <button className="save-button" onClick={handleUpdateItem}>
              Update {category}
            </button>
            <button
              className="cancel-button"
              onClick={() => setEditingItem(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="items-list">
        <h3>
          {category}s ({items.length})
        </h3>
        <ItemsList
          items={items}
          category={category}
          categoryFields={categoryFields}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
        />
      </div>
    </div>
  );
};

CategoryManager.propTypes = {
  category: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CategoryManager;
